<template>
  <div class="h-auto overflow-auto flex flex-col">
    <woot-modal-header
      :header-title="
        item.id ? $t('CSAT_MGMT.EDIT.TITLE') : $t('CSAT_MGMT.ADD.TITLE')
      "
      :header-content="$t('CSAT_MGMT.ADD.DESC')"
    />
    <form class="mx-0 flex flex-wrap !p-0 w-full" @submit.prevent="submit">
      <div class="flex flex-col gap-4 px-8 p-4 w-full">
        <div class="flex flex-row gap-4">
          <div class="w-2/5">
            <woot-input
              v-model="title"
              :class="{ error: $v.title.$error }"
              class="w-full label-name--input"
              :label="$t('CSAT_MGMT.FORM.TITLE.LABEL')"
              :placeholder="$t('CSAT_MGMT.FORM.TITLE.PLACEHOLDER')"
              :error="getTitleErrorMessage"
              @input="$v.title.$touch"
            />

            <resizable-text-area
              v-model.trim="description"
              type="text"
              rows="1"
              :class="{ error: $v.description.$error }"
              class="w-full"
              :label="$t('CSAT_MGMT.FORM.DESCRIPTION.LABEL')"
              :placeholder="$t('CSAT_MGMT.FORM.DESCRIPTION.PLACEHOLDER')"
              @input="$v.description.$touch"
            />

            <resizable-text-area
              v-model.trim="welcomeMessage"
              type="text"
              rows="1"
              :class="{ error: $v.welcomeMessage.$error }"
              class="w-full min-h-[4rem]"
              :label="$t('CSAT_MGMT.FORM.WELCOME_MESSAGE.LABEL')"
              :placeholder="$t('CSAT_MGMT.FORM.WELCOME_MESSAGE.PLACEHOLDER')"
              @input="$v.welcomeMessage.$touch"
            />

            <div class="flex">
              <span class="whitespace-nowrap mx-2">
                {{ $t('CSAT_MGMT.FORM.LTR') }}
              </span>
              <woot-switch v-model="isRtl" class="self-center" />
              <span class="whitespace-nowrap mx-2">
                {{ $t('CSAT_MGMT.FORM.RTL') }}
              </span>
            </div>
          </div>

          <div class="w-3/5 min-h-[200px] text-center">
            <new-customer-satisfaction
              :questions="questions"
              :disabled="true"
              :is-rtl="isRtl"
            />
          </div>
        </div>

        <div class="min-h-[300px]">
          <table class="questions-table table table-striped table-auto w-full">
            <thead class="thead-dark">
              <tr>
                <th scope="col" />
                <th scope="col">
                  {{ $t('CSAT_MGMT.QUESTION_BUILDER.SETUP_QUESTIONS.TITLE') }}
                </th>
                <th scope="col">
                  {{ $t('CSAT_MGMT.QUESTION_BUILDER.SETUP_QUESTIONS.TYPE') }}
                </th>
                <th scope="col">
                  {{
                    $t('CSAT_MGMT.QUESTION_BUILDER.SETUP_QUESTIONS.MANDATORY')
                  }}
                </th>
                <th scope="col">
                  {{ $t('CSAT_MGMT.QUESTION_BUILDER.SETUP_QUESTIONS.OPTIONS') }}
                </th>
              </tr>
            </thead>
            <setup-survey-questions
              :questions="questions"
              @update="handleQuestionsUpdate"
              @delete="handleQuestionDelete"
            />
            <tfoot>
              <tr v-if="questions.length < 3">
                <td colspan="7" class="text-center">
                  <woot-button
                    variant="smooth"
                    color-scheme="secondary"
                    size="tiny"
                    @click="handleQuestionsAdded"
                  >
                    {{
                      $t(
                        'CSAT_MGMT.QUESTION_BUILDER.SETUP_QUESTIONS.ADD_QUESTION'
                      )
                    }}
                  </woot-button>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <div class="flex justify-end items-center pb-8 px-8 gap-2 w-full">
        <woot-button class="button clear" @click.prevent="onClose">
          {{ $t('CSAT_MGMT.FORM.CANCEL') }}
        </woot-button>
        <woot-button
          type="submit"
          :is-disabled="
            $v.title.$invalid || $v.questions.$invalid || uiFlags.isSubmitting
          "
          :is-loading="uiFlags.isSubmitting"
        >
          {{
            item.id ? $t('CSAT_MGMT.FORM.UPDATE') : $t('CSAT_MGMT.FORM.CREATE')
          }}
        </woot-button>
      </div>
    </form>
  </div>
</template>

<script>
import alertMixin from 'shared/mixins/alertMixin';
import validationMixin from '../validationMixin';
import { mapGetters } from 'vuex';
import validations from '../validations';
import { DEFAULT_QUESTION_VALUES } from '../constants';
import SetupSurveyQuestions from './SetupSurveyQuestions.vue';
import NewCustomerSatisfaction from '../../../../../../shared/components/NewCustomerSatisfaction.vue';
import ResizableTextArea from '../../../../../../shared/components/ResizableTextArea.vue';

export default {
  components: {
    SetupSurveyQuestions,
    NewCustomerSatisfaction,
    ResizableTextArea,
  },
  mixins: [alertMixin, validationMixin],
  props: {
    item: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      description: '',
      title: '',
      welcomeMessage: '',
      questions: [],
      DEFAULT_QUESTION_VALUES,
      isRtl: false,
    };
  },
  validations,
  computed: {
    ...mapGetters({
      uiFlags: 'csatSurveys/getUIFlags',
    }),
  },
  watch: {
    item: {
      immediate: true,
      handler(newValue) {
        if (newValue && Object.keys(newValue).length > 0) {
          this.title = newValue.title || '';
          this.description = newValue.description || '';
          this.welcomeMessage = newValue.welcome_message || '';
          this.questions = newValue.questions || [];
          this.isRtl = newValue.is_rtl || false;
        }
      },
    },
  },
  methods: {
    onClose() {
      this.$emit('close');
    },
    async add() {
      try {
        await this.$store.dispatch('csatSurveys/create', {
          description: this.description.trim(),
          title: this.title.trim(),
          welcome_message: this.welcomeMessage.trim(),
          questions: this.questions,
          is_rtl: this.isRtl,
        });
        this.showAlert(this.$t('CSAT_MGMT.ADD.API.SUCCESS_MESSAGE'));
        this.onClose();
      } catch (error) {
        const errorMessage =
          error.message || this.$t('CSAT_MGMT.ADD.API.ERROR_MESSAGE');
        this.showAlert(errorMessage);
      }
    },
    async update() {
      try {
        await this.$store.dispatch('csatSurveys/update', {
          id: this.item.id,
          description: this.description.trim(),
          welcome_message: this.welcomeMessage.trim(),
          title: this.title.trim(),
          questions: this.questions,
          is_rtl: this.isRtl,
        });
        this.showAlert(this.$t('CSAT_MGMT.EDIT.API.SUCCESS_MESSAGE'));
        this.onClose();
      } catch (error) {
        const errorMessage =
          error.message || this.$t('CSAT_MGMT.EDIT.API.ERROR_MESSAGE');
        this.showAlert(errorMessage);
      }
    },
    submit() {
      if (this.item.id) {
        this.update();
      } else {
        this.add();
      }
    },
    handleQuestionsAdded() {
      this.questions.push({
        ...DEFAULT_QUESTION_VALUES,
        id:
          'qs_' +
          Date.now().toString(36) +
          Math.random().toString(36).substr(2),
      });
    },
    handleQuestionsUpdate(updatedQuestions) {
      this.questions = updatedQuestions;
    },
    handleQuestionDelete(item) {
      this.questions = this.questions.filter(i => i.key !== item.key);
    },
  },
};
</script>
<style lang="scss" scoped></style>
